/**
 * Copyright 2025 Phenix Real Time Solutions, Inc. Confidential and Proprietary. All Rights Reserved.
 */

import {Component} from 'preact';
import PropTypes from 'prop-types';
import GUMConfigurationView from './components/GUMConfigurationView';
import userDataStore from '../shared/userDataStore';

class GUMConfiguration extends Component {
  constructor(props) {
    super(props);
    this.state = {
      audioSources: [],
      videoSources: [],
      userMedia: null
    };
    this.videoOnly = props.videoOnly;
    this.audioOnly = props.audioOnly;
    this.defaultAudioSource = this.videoOnly ? 'disabled' : userDataStore.get('AudioSelect' + (this.props.storeLocalStateKey || ''));
    this.defaultVideoSource = this.audioOnly ? 'disabled' : userDataStore.get('VideoSelect' + (this.props.storeLocalStateKey || ''));
    this.deviceOptions = {
      audio: {deviceId: this.defaultAudioSource},
      video: {deviceId: this.defaultVideoSource}
    };

    if (this.defaultAudioSource === 'none' || !this.defaultAudioSource) {
      this.deviceOptions.audio = {};
    }

    if (this.defaultVideoSource === 'none' || !this.defaultVideoSource) {
      this.deviceOptions.video = {};
    }

    this.changeSelectedDevices = this.changeSelectedDevices.bind(this);
    this.onVideoSourceSelect = this.onVideoSourceSelect.bind(this);
    this.onAudioSourceSelect = this.onAudioSourceSelect.bind(this);
    this.setUserMedia = this.setUserMedia.bind(this);
    this.confirmUserMedia = this.confirmUserMedia.bind(this);
  }

  componentDidUpdate(prevProps) {
    const {aspectRatio, resolution, frameRate} = this.props;
    const aspectRatioChanged = prevProps.aspectRatio !== aspectRatio;
    const resolutionChanged = prevProps.resolution !== resolution;
    const frameRateChanged = prevProps.frameRate !== frameRate;

    if (aspectRatioChanged || resolutionChanged || frameRateChanged) {
      this.changeSelectedDevices();
    }
  }

  componentDidMount() {
    this.changeSelectedDevices();

    navigator.mediaDevices.enumerateDevices().then(devices => {
      const sources = devices.map(device => {
        if (device.kind === 'audioinput') {
          return {
            kind: 'audio',
            id: device.deviceId,
            label: device.label
          };
        } else if (device.kind === 'videoinput') {
          return {
            kind: 'video',
            id: device.deviceId,
            label: device.label
          };
        }

        return null;
      }).filter(Boolean);
      const defaultAudioSource = sources.find(source => source.label.toLowerCase() === 'default' && source.kind === 'audio');
      const defaultVideoSource = sources.find(source => source.label.toLowerCase() === 'default' && source.kind === 'video');
      const videoSources = sources.filter(source => source.kind === 'video');
      const audioSources = sources.filter(source => source.kind === 'audio');
      let hasChanges = false;

      if (defaultAudioSource && !this.defaultAudioSource) {
        this.deviceOptions.audio.deviceId = defaultAudioSource.id;
        hasChanges = true;
      }

      if (defaultVideoSource && !this.defaultVideoSource) {
        this.deviceOptions.video.deviceId = defaultVideoSource.id;
        hasChanges = true;
      }

      if (!defaultAudioSource && !this.defaultAudioSource && audioSources.length === 1) {
        this.deviceOptions.audio.deviceId = audioSources[0].id;
        hasChanges = true;
      }

      if (!defaultVideoSource && !this.defaultVideoSource && videoSources.length === 1) {
        this.deviceOptions.video.deviceId = videoSources[0].id;
        hasChanges = true;
      }

      if (hasChanges) {
        this.changeSelectedDevices();
      }

      this.setState({
        videoSources,
        audioSources
      });
    });
  }

  componentWillUnmount() {
    this.disposeUserMedia();
  }

  onVideoSourceSelect(deviceId) {
    this.deviceOptions.video = {deviceId};
    this.changeSelectedDevices();
    userDataStore.put('VideoSelect' + (this.props.storeLocalStateKey || ''), deviceId);
  }

  onAudioSourceSelect(deviceId) {
    this.deviceOptions.audio = {deviceId};
    this.changeSelectedDevices();
    userDataStore.put('AudioSelect' + (this.props.storeLocalStateKey || ''), deviceId);
  }

  changeSelectedDevices() {
    let deviceOptions = {...this.deviceOptions};

    if (deviceOptions.video && deviceOptions.video.deviceId === 'screen') {
      delete deviceOptions.video;
      deviceOptions.screen = true;
    } else {
      delete deviceOptions.screen;
    }

    if (!deviceOptions.audio || !deviceOptions.audio.deviceId || deviceOptions.audio.deviceId === 'no-source' || deviceOptions.audio.deviceId === 'disabled') {
      deviceOptions.audio = false;
    }

    if (!deviceOptions.video || !deviceOptions.video.deviceId || deviceOptions.video.deviceId === 'no-source' || deviceOptions.video.deviceId === 'disabled') {
      deviceOptions.video = false;
    }

    this.disposeUserMedia();

    if (deviceOptions.video || deviceOptions.audio) {
      if (this.props.resolution) {
        const dimensions = this.props.aspectRatio.split('x');
        const ratio = parseInt(dimensions[0], 10) / parseInt(dimensions[1], 10);

        deviceOptions.video = {
          ...deviceOptions.video,
          frameRate: {ideal: this.props.frameRate},
          width: {ideal: parseInt(this.props.resolution * ratio, 10)},
          height: {ideal: parseInt(this.props.resolution, 10)}
        };
      }

      navigator.mediaDevices
        .getUserMedia({
          audio: deviceOptions.audio,
          video: deviceOptions.video
        })
        .then(stream => {
          const videoTracks = stream.getVideoTracks();

          if (!videoTracks || videoTracks.length === 0) {
            return;
          }

          const trackCapabilities = videoTracks[0].getCapabilities();
          const frameRateMax = trackCapabilities.frameRate && trackCapabilities.frameRate.max ? parseInt(trackCapabilities.frameRate.max, 10) : 30;
          const resolutionMax = trackCapabilities.height && trackCapabilities.height.max ? parseInt(trackCapabilities.height.max, 10) : 0;

          if (this.props.frameRate > frameRateMax) {
            this.props.changeFrameRate(this.props.frameRate, frameRateMax);
          }

          this.setUserMedia(null, {
            userMedia: stream,
            constraints: {
              aspectRatio: this.props.aspectRatio,
              resolution: resolutionMax,
              frameRate: frameRateMax
            }
          });
        })
        .catch(error => {
          this.setUserMedia(error);
        });
    } else if (this.props.gotUserMedia) {
      this.setUserMedia(null, {
        userMedia: null,
        constraints: null
      });
    }
  }

  setUserMedia(error, {userMedia, constraints} = {}) {
    if (userMedia) {
      this.userMediaToDispose = userMedia;
    }

    this.setState({userMedia});

    if (this.props.gotUserMedia && !this.audioOnly) {
      this.props.gotUserMedia(error, {
        userMedia,
        constraints
      });
    }
  }

  disposeUserMedia() {
    if (this.userMediaToDispose) {
      this.userMediaToDispose.getTracks().forEach(track => track.stop());
      delete this.userMediaToDispose;
    }
  }

  confirmUserMedia() {
    delete this.userMediaToDispose;
    this.props.confirmUserMedia(this.state.userMedia);
  }

  render() {
    const {style = {}, classNames = {}, confirmButtonText, confirmButtonDisabled, includeScreen} = this.props;

    return (
      <GUMConfigurationView
        propsStyle={style}
        classNames={classNames}
        videoOnly={this.videoOnly}
        audioOnly={this.audioOnly}
        audioSources={this.state.audioSources}
        videoSources={this.state.videoSources}
        includeScreen={includeScreen}
        confirmDevices={this.confirmUserMedia}
        confirmButtonText={confirmButtonText}
        confirmButtonDisabled={confirmButtonDisabled}
        audioMediaStream={this.state.userMedia}
        videoMediaStream={this.state.userMedia}
        selectedAudioSource={this.deviceOptions.audio.deviceId || ''}
        selectedVideoSource={this.deviceOptions.video.deviceId || ''}
        onAudioSourceSelect={this.onAudioSourceSelect}
        onVideoSourceSelect={this.onVideoSourceSelect} />
    );
  }
}

GUMConfiguration.propTypes = {
  videoOnly: PropTypes.bool,
  audioOnly: PropTypes.bool,
  confirmUserMedia: PropTypes.func.isRequired,
  includeScreen: PropTypes.bool,
  gotUserMedia: PropTypes.func,
  changeFrameRate: PropTypes.func,
  confirmButtonText: PropTypes.string,
  confirmButtonDisabled: PropTypes.bool,
  classNames: PropTypes.object,
  style: PropTypes.object,
  aspectRatio: PropTypes.string,
  resolution: PropTypes.string,
  frameRate: PropTypes.number,
  storeLocalStateKey: PropTypes.string
};

export default GUMConfiguration;